import { ElementType, useEffect } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { CloseStrokeIcon } from "../../icons/CloseStroke"
import { Typography } from "../../typography"
import { DeprecatedButton } from "../button"

const WizardOverlay = styled.div`
  width: 100%;
  height: 100vh;
  background: #fff;
`

const Gradient = styled.div`
  background: linear-gradient(90deg, #a97eff 0%, #df5ea3 24.57%, #ffb571 49.48%, #f1dfce 63.76%, #9effad 100%);
  left: 0;
  height: 2px;
`

const HeaderContainer = styled.div`
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.light};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.$200};
`

const WizardQuitButton = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
`

const StepperWrapper = styled.div`
  width: 50rem;
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`

const StepperItem = styled.div<{ $completed: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  &:before {
    position: absolute;
    content: "";
    border-bottom: 2px solid ${({ theme }) => theme.colors.grey.$400};
    border-bottom-style: dotted;
    width: 100%;
    top: 0.5rem;
    left: -50%;
    z-index: 2;
  }

  &:after {
    position: absolute;
    content: "";
    border-bottom: 2px solid ${({ theme }) => theme.colors.grey.$400};
    border-bottom-style: dotted;
    width: 100%;
    top: 0.5rem;
    left: 50%;
    z-index: 2;
  }

  &:first-child::before {
    content: none;
  }

  &:last-child::after {
    content: none;
  }

  ${({ theme, $completed }) =>
    $completed &&
    `
    ::after {
      position: absolute;
      content: "";
      border-bottom: 2px solid ${theme.colors.grey.$600};
      width: 100%;
      top: 0.5rem;
      left: 50%;
      z-index: 3;
    }
  `}
`

const StepName = styled(Typography)<{ $active: boolean }>`
  color: ${({ theme, $active }) => ($active ? theme.colors.grey.$600 : theme.colors.grey.$400)};
`

const StepCounter = styled.div<{ $active: boolean }>`
  position: relative;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: ${({ theme, $active }) => ($active ? theme.colors.grey.$600 : theme.colors.grey.$300)};
  margin-bottom: 0.5rem;
`

export type DeprecatedWizardStep = {
  name: string
  active: boolean
  completed: boolean
}

export type WizardHeaderProps = {
  onQuit: () => void
  steps: DeprecatedWizardStep[]
  headerProps?: React.ComponentProps<typeof HeaderContainer>
  gradientProps?: React.ComponentProps<typeof Gradient>
  stepperWrapperProps?: React.ComponentProps<typeof StepperWrapper>
  stepperItemProps?: React.ComponentProps<typeof StepperItem>
  stepNameProps?: React.ComponentProps<typeof StepName>
  stepCounterProps?: React.ComponentProps<typeof StepCounter>
  wizardQuitButtonProps?: React.ComponentProps<typeof DeprecatedButton>
}

export const DeprecatedWizardHeader = ({
  onQuit,
  steps,
  headerProps,
  gradientProps,
  stepperWrapperProps,
  stepperItemProps,
  stepNameProps,
  stepCounterProps,
  wizardQuitButtonProps,
}: WizardHeaderProps) => {
  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onQuit()
      }
    }
    document.addEventListener("keydown", keyListener)
    return () => document.removeEventListener("keydown", keyListener)
  }, [onQuit])

  return (
    <HeaderContainer {...headerProps}>
      <Gradient {...gradientProps} />

      {steps?.length > 0 ? (
        <StepperWrapper {...stepperWrapperProps}>
          {steps.map(({ name, active, completed }: DeprecatedWizardStep) => (
            <StepperItem key={name} $completed={completed} {...stepperItemProps}>
              <StepCounter $active={active || completed} {...stepCounterProps} />
              <StepName $variant="body-4-regular" $active={active || completed} {...stepNameProps}>
                {name}
              </StepName>
            </StepperItem>
          ))}
        </StepperWrapper>
      ) : null}

      <WizardQuitButton>
        <DeprecatedButton
          $variant="round"
          onClick={onQuit}
          leftIcon={CloseStrokeIcon}
          isLoading={false}
          {...wizardQuitButtonProps}
        />
      </WizardQuitButton>
    </HeaderContainer>
  )
}

export type WizardProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    onQuit: () => void
    steps: DeprecatedWizardStep[]
  },
  C,
  C
>

export const DeprecatedWizard = <C extends ElementType>({
  children,
  onQuit,
  steps,
  wizardQuitButtonProps,
  ...props
}: WizardProps<C>) => {
  return (
    <WizardOverlay {...props}>
      <DeprecatedWizardHeader onQuit={onQuit} steps={steps} wizardQuitButtonProps={wizardQuitButtonProps} />
      {children}
    </WizardOverlay>
  )
}
